import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { sendMessage, ChatMessage, getLastUpdate } from '../services/api';
import { Message, MessageBubble } from './MessageBubble';
import { useTheme } from '../ThemeContext';

const ThemeToggle: React.FC = () => {
  const { mode, setMode } = useTheme();
  const next = mode === 'light' ? 'dark' : mode === 'dark' ? 'auto' : 'light';
  const icon = mode === 'light' ? '☀️' : mode === 'dark' ? '🌙' : '⚙️';
  const title = mode === 'light' ? 'Light (klikni pre Dark)' : mode === 'dark' ? 'Dark (klikni pre Auto)' : 'Auto (klikni pre Light)';

  return (
    <button
      onClick={() => setMode(next)}
      title={title}
      style={{
        background: 'rgba(255,255,255,0.2)',
        border: 'none',
        width: 32,
        height: 32,
        borderRadius: '50%',
        cursor: 'pointer',
        fontSize: 16,
        lineHeight: '32px',
      }}
    >
      {icon}
    </button>
  );
};

interface ChatWindowProps {
  sessionId: string;
  sessionStart: string;
  onQuerySent: () => void;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

export const ChatWindow: React.FC<ChatWindowProps> = ({ sessionId, sessionStart, onQuerySent, messages, setMessages }) => {
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!loading) {
      inputRef.current?.focus();
    }
  }, [loading]);

  useEffect(() => {
    getLastUpdate()
      .then((date) => setLastUpdate(date))
      .catch(() => {});
  }, []);

  const resetSession = () => {
    setMessages([{ role: 'assistant', content: 'Dobrý deň! Som asistent obce Vinné. Opýtajte sa ma čokoľvek o obci.' }]);
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    if (inputRef.current) inputRef.current.style.height = 'auto';
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setLoading(true);

    try {
      const history: ChatMessage[] = messages
        .slice(1)
        .slice(-10)
        .map((m) => ({ role: m.role, content: m.content }));

      const response = await sendMessage(text, history, sessionId);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: response.answer, sources: response.sources },
      ]);
      onQuerySent();
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Prepáčte, nastala chyba. Skúste to znova.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxWidth: 700, margin: '0 auto' }}>
      <div style={{ padding: '16px 20px', background: 'var(--header-bg)', color: '#fff', borderRadius: '12px 12px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img
            src={`${process.env.PUBLIC_URL}/favicon.svg`}
            alt=""
            style={{ width: 32, height: 32, cursor: 'pointer' }}
            onClick={() => navigate('/stats')}
            title="Zobraziť štatistiky"
          />
          <div>
            <h1 style={{ margin: 0, fontSize: 18 }}>GPT Asistent obce Vinné</h1>
            <div style={{ fontSize: 12, opacity: 0.8 }}>Powered by <a href="https://www.lab463.com" target="_blank" rel="noopener noreferrer" style={{ color: '#fff', textDecoration: 'none' }}>lab463.com</a></div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ThemeToggle />
          <button
            onClick={() => setShowInfo(!showInfo)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: '#fff',
              width: 32,
              height: 32,
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: 16,
              fontWeight: 'bold',
              lineHeight: '32px',
            }}
            title="Informácie"
          >
            i
          </button>
        </div>
      </div>

      <div style={{
        padding: '8px 16px',
        background: 'var(--bg-footer)',
        borderBottom: '1px solid var(--border-color)',
        color: 'var(--text-muted)',
        fontSize: 10,
        textAlign: 'center',
        lineHeight: 1.5,
      }}>
        Táto stránka je nezávislý projekt a nie je oficiálnou stránkou obce Vinné.
        Informácie sú generované automaticky z verejne dostupných dokumentov.
        Prevádzkovateľ nenesie zodpovednosť za prípadné nepresnosti.
      </div>

      {showInfo && (
        <div style={{
          padding: '14px 20px',
          background: 'var(--bg-info)',
          borderBottom: `1px solid var(--border-info)`,
          fontSize: 13,
          color: 'var(--text-info)',
          lineHeight: 1.6,
        }}>
          <strong>O asistentovi</strong>
          <p style={{ margin: '8px 0 4px' }}>
            Dáta pochádzajú z oficiálnej webstránky obce Vinné (<a href="https://www.vinne.sk" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>vinne.sk</a>) — HTML stránky, zmluvy, faktúry, objednávky, zápisnice, VZN a ďalšie zverejnené dokumenty.
          </p>
          <p style={{ margin: '4px 0' }}>
            Vytvorené v <a href="https://lab463.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>lab463</a> ako experiment #463.2026.4.          </p>
          {lastUpdate && (
            <p style={{ margin: '4px 0', fontSize: 12 }}>
              Dáta aktualizované: {new Date(lastUpdate).toLocaleDateString('sk-SK', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
          <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
            Asistent nemusí byť vždy presný. Pre overenie informácií navštívte priamo vinne.sk.
          </p>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto', padding: 16, background: 'var(--bg-chat)' }}>
        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}
        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', color: 'var(--text-muted)', fontSize: 14 }}>
            <div style={{ display: 'flex', gap: 4 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', opacity: 0.7, animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0s' }} />
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', opacity: 0.7, animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0.2s' }} />
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', opacity: 0.7, animation: 'bounce 1.4s infinite ease-in-out both', animationDelay: '0.4s' }} />
            </div>
            <span>Premýšľam...</span>
            <style>{`@keyframes bounce { 0%, 80%, 100% { transform: scale(0); } 40% { transform: scale(1); } }`}</style>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div style={{ padding: '12px 12px', background: 'var(--bg-input)' }}>
        <div style={{
          border: '1px solid var(--border-color)',
          borderRadius: 24,
          background: 'var(--bg-chat)',
          padding: '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}>
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Napíšte otázku o obci Vinné..."
            disabled={loading}
            rows={1}
            style={{
              width: '100%',
              boxSizing: 'border-box',
              padding: 0,
              border: 'none',
              fontSize: 16,
              outline: 'none',
              background: 'transparent',
              color: 'var(--text-primary)',
              resize: 'none',
              fontFamily: 'inherit',
              lineHeight: 'inherit',
              overflow: 'hidden',
            }}
          />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <button
              onClick={resetSession}
              disabled={loading || messages.length <= 1}
              style={{
                padding: 6,
                background: 'none',
                color: loading || messages.length <= 1 ? 'var(--text-muted)' : 'var(--text-primary)',
                border: 'none',
                borderRadius: '50%',
                cursor: loading || messages.length <= 1 ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                opacity: loading || messages.length <= 1 ? 0.4 : 0.7,
              }}
              title="Začať novú konverzáciu"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 6h18M8 6V4a1 1 0 011-1h6a1 1 0 011 1v2M5 6v14a2 2 0 002 2h10a2 2 0 002-2V6" />
              </svg>
            </button>
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              style={{
                width: 32,
                height: 32,
                padding: 0,
                background: loading || !input.trim() ? 'var(--text-muted)' : 'var(--accent)',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                cursor: loading || !input.trim() ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: loading || !input.trim() ? 0.4 : 1,
                transition: 'opacity 0.15s, background 0.15s',
              }}
              title="Odoslať"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="19" x2="12" y2="5" />
                <polyline points="5 12 12 5 19 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div style={{
        padding: '10px 20px',
        background: 'var(--bg-footer)',
        color: 'var(--text-muted)',
        fontSize: 11,
        textAlign: 'center',
        borderTop: '1px solid var(--border-color)',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
      }}>
        <span>🧪 Experiment #463.2026.4</span>
        <span style={{ margin: '0 4px' }}>—</span>
        <br className="mobile-break" />
        <span>Obecný AI asistent — „Už len moja žena zná vecej..."</span>
        <br />
        <span>
          Built in{' '}
          <a href="https://www.lab463.com" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
            lab463
          </a>
          {' '}·{' '}
          <a href="mailto:dexter@lab463.com" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
            dexter@lab463.com
          </a>
        </span>
      </div>
    </div>
  );
};
